import { createOvalChecker } from './oval'
import { encodeFrame, type EncodeOptions } from './encode'

/**
 * One open camera. The page never keeps a frame: `grab()` returns a fresh Blob, held in memory until the upload,
 * and `stop()` releases every track of the stream (and is called on unmount, after the upload and on every error).
 */
export interface CameraSession {
  /** True while a face sits inside the oval guide. */
  faceInOval(): boolean | Promise<boolean>
  grab(opts: EncodeOptions): Promise<Blob>
  /** The element to measure motion on (the motion trigger of Task 22 reads it). */
  video: HTMLVideoElement
  stop(): void
}

/** The seam tests replace: opens the camera on the given video element. */
export interface Camera {
  open(video: HTMLVideoElement): Promise<CameraSession>
}

export class CameraError extends Error {
  constructor(public readonly reason: 'denied' | 'not_found' | 'unsupported' | 'busy') {
    super(`camera_${reason}`)
    this.name = 'CameraError'
  }
}

/** The constraints of the shared camera screen (copy v8 "S4 own camera screen"). */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: { facingMode: 'user', height: { ideal: 720, min: 480 } },
  audio: false,
}

function reasonOf(err: unknown): CameraError['reason'] {
  const name = (err as { name?: string } | null)?.name
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied'
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'not_found'
  if (name === 'NotReadableError' || name === 'AbortError') return 'busy'
  return 'unsupported'
}

/** The real camera: getUserMedia, a `<video>` for the picture and a canvas for the frames. */
export const browserCamera: Camera = {
  async open(video) {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) throw new CameraError('unsupported')
    let stream: MediaStream
    try {
      stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS)
    } catch (err) {
      throw new CameraError(reasonOf(err))
    }
    const stop = () => {
      for (const track of stream.getTracks()) track.stop()
      video.srcObject = null
    }
    try {
      video.srcObject = stream
      video.muted = true
      video.setAttribute('playsinline', 'true')
      await video.play()
    } catch (err) {
      stop()
      throw new CameraError(reasonOf(err))
    }
    const oval = createOvalChecker(video)
    return {
      video,
      faceInOval: () => oval.check(),
      grab: (opts) => encodeFrame(video, opts),
      stop,
    }
  },
}
